import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import ReactS3Uploader from "react-s3-uploader";
import ajax from "superagent";
import { SelfieFilter } from "./filterTypes";
import WebcamCapture from "../../MasterImageSearch/components/SelfieUploadPopup/WebcamCapture";
import { isCameraAvailable } from "../../../utils/cameraAvailability";

const placeholderIcon = require("../../MasterImageSearch/images/selfie-img.svg");
const delImg = require("../../MasterImageSearch/styles/delete.svg");

interface SelfieSearchDropdownProps {
  value: SelfieFilter;
  onChange: (updated: SelfieFilter) => void;
  onClose: () => void;
  selfieSearchS3Bucket?: string;
}

const SelfieSearchDropdown: React.FC<SelfieSearchDropdownProps> = ({
  value,
  onChange,
  onClose,
  selfieSearchS3Bucket = "geosnapshot-selfie-search"
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewImage, setPreviewImage] = useState<string | null>(
    value.imageUrl
  );
  const [imageUploadStart, setImageUploadStart] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);

  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  useEffect(() => {
    setPreviewImage(value.imageUrl);
  }, [value.imageUrl]);

  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith("blob:")) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const imageSelected = (imageFile: File, next: (imageFile: File) => void) => {
    setImageUploadStart(true);
    if (previewImage && previewImage.startsWith("blob:")) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(URL.createObjectURL(imageFile));
    next(imageFile);
  };

  const getSignedUrl = async (file: File): Promise<{ signedUrl: string }> => {
    try {
      const data = await ajax.get("/s3/signed_url").query({
        bucket: selfieSearchS3Bucket,
        contentType: file.type
      });
      return JSON.parse(data.text);
    } catch (error) {
      console.error(error);
      // @ts-ignore
      toastr.error("Failed to get upload URL. Please try again.");
      throw error;
    }
  };

  const clearSelfie = () => {
    onChange({ imageUrl: null });
    if (previewImage && previewImage.startsWith("blob:")) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
    setUploadProgress(0);
    setImageUploadStart(false);
  };

  const handleUploadFinish = (result: any) => {
    setUploadProgress(100);
    const uploadedUrl = result.signedUrl.split("?")[0];
    onChange({ imageUrl: uploadedUrl });
    setImageUploadStart(false);
  };

  const handleUploadError = (error: any) => {
    console.error(error);
    // @ts-ignore
    toastr.error("Failed to upload image. Please try again.");
    setImageUploadStart(false);
    setUploadProgress(0);
  };

  const toggleWebcam = () => {
    setShowWebcam(!showWebcam);
  };

  const handleWebcamCapture = async (base64Image: string) => {
    setShowWebcam(false);

    try {
      // Convert base64 to blob and trigger upload via existing infrastructure
      const res = await fetch(base64Image);
      const blob = await res.blob();
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

      // Set preview immediately
      setPreviewImage(base64Image);
      setImageUploadStart(true);

      // Get signed URL for upload
      const params = await getSignedUrl(file);

      await ajax
        .put(params.signedUrl)
        .set("Content-Type", file.type)
        .set("x-amz-acl", "public-read")
        .send(file);

      handleUploadFinish({ signedUrl: params.signedUrl });
    } catch (error) {
      console.error(error);
      handleUploadError(error);
      // @ts-ignore
      toastr.error("Failed to process selfie. Please try again.");
    }
  };

  // Wrapper for ReactS3Uploader which expects callback-based getSignedUrl
  const getSignedUrlCallback = (
    file: File,
    callback: (params: { signedUrl: string }) => void
  ) => {
    getSignedUrl(file)
      .then(params => callback(params))
      .catch(error => {
        // Error already handled in getSignedUrl, just prevent unhandled rejection
        console.error("getSignedUrl failed:", error);
      });
  };

  const uploaderProps = {
    className: "dv-upload-input",
    style: { display: "none" },
    getSignedUrl: getSignedUrlCallback,
    accept: "image/*",
    preprocess: imageSelected,
    onProgress: setUploadProgress,
    onError: handleUploadError,
    onFinish: handleUploadFinish,
    uploadRequestHeaders: { "x-amz-acl": "public-read" },
    scrubFilename: (filename: string) => filename.replace(/[^\w\d_\-.]+/gi, ""),
    contentDisposition: "auto" as const
  };

  return ReactDOM.createPortal(
    <div>
      <div
        className={`modal fade in ns geo-modal geo-modal--scroll ${
          showWebcam ? "ns-modal-large" : "geo-selfie-popup"
        } modal-show`}
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog filter-dialog-content">
          <button type="button" className="close" onClick={onClose}>
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="filter-content-div">
            <p className="filter-header-inner">
              {showWebcam ? "Take a Selfie" : "Upload a Selfie"}
            </p>

            {showWebcam ? (
              <WebcamCapture
                closeWebcam={toggleWebcam}
                captured={handleWebcamCapture}
              />
            ) : (
              <>
                <div className="filter-selfie-img-div">
                  {previewImage ? (
                    <>
                      <img
                        src={previewImage}
                        alt="Selfie preview"
                        className={imageUploadStart ? "uploading" : ""}
                      />
                      <span className="div-del">
                        <button
                          type="button"
                          className="selfie-del-btn"
                          onClick={clearSelfie}
                          aria-label="Delete selfie"
                        >
                          <img src={delImg} alt="Delete" />
                        </button>
                      </span>
                    </>
                  ) : (
                    <img
                      className="bit-m"
                      src={placeholderIcon}
                      alt="Upload selfie"
                    />
                  )}

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="uploader">
                      <span
                        className="uploader-fill"
                        style={{ height: `${uploadProgress}%` }}
                      ></span>
                      <span className="uploader-count">{uploadProgress}%</span>
                    </div>
                  )}
                </div>

                <p className="filter-selfie-p1">
                  Please upload or take a photo looking straight ahead to find
                  matching photos.
                </p>

                <div className="selfie-btns">
                  <label
                    className="btn btn-primary d-block selfie-up-btn"
                    style={{ gap: 5 }}
                  >
                    <i className="fa fa-upload"></i>
                    <span>
                      {previewImage ? "Change Photo" : "Upload Photo"}
                    </span>
                    <ReactS3Uploader {...uploaderProps} />
                  </label>
                  {isCameraAvailable() && (
                    <button
                      type="button"
                      className="btn secondary-btn webcam-component btn-take-selfie"
                      onClick={toggleWebcam}
                      style={{ gap: 5 }}
                    >
                      <i className="fa fa-camera"></i>
                      <span>Take a selfie</span>
                    </button>
                  )}
                </div>

                <p className="filter-selfie-p2">
                  To protect your privacy, your selfie image is not saved after
                  searching. By uploading your photo, you agree to biometric
                  data from your selfie being used only for searching for photos
                  in accordance with our{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms & Conditions
                  </a>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" onClick={onClose} />
    </div>,
    document.body
  );
};

export default SelfieSearchDropdown;
